<template>
  <div class="h-full w-full min-h-0 flex flex-col gap-0 text-gray-800">
    <section class=" bg-white p-3 min-h-24">
      <div v-if="selectedEvent" class="resource-selection-info grid gap-3 text-left sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8">
        <div class="sm:col-span-2 xl:col-span-2">
          <dt class="text-xs font-semibold uppercase tracking-wide text-gray-500">Fixture</dt>
          <dd class="text-sm font-medium text-gray-900">{{ selectedEvent.fixture || selectedEvent.name || '—' }}</dd>
        </div>
        <div>
          <dt class="text-xs font-semibold uppercase tracking-wide text-gray-500">Service start</dt>
          <dd class="text-sm text-gray-900">{{ formatUtc(selectedEvent.serviceStartTime) }}</dd>
        </div>
        <div>
          <dt class="text-xs font-semibold uppercase tracking-wide text-gray-500">Line-up</dt>
          <dd class="text-sm text-gray-900">{{ formatUtc(selectedEvent.lineupTime) }}</dd>
        </div>
        <div>
          <dt class="text-xs font-semibold uppercase tracking-wide text-gray-500">Kick-off</dt>
          <dd class="text-sm text-gray-900">{{ formatUtc(selectedEvent.startTime) }}</dd>
        </div>
        <div>
          <dt class="text-xs font-semibold uppercase tracking-wide text-gray-500">End</dt>
          <dd class="text-sm text-gray-900">{{ formatUtc(selectedEvent.endTime) }}</dd>
        </div>
        <div>
          <dt class="text-xs font-semibold uppercase tracking-wide text-gray-500">Input</dt>
          <dd class="text-sm text-gray-900">{{ selectedEvent.inputName || '—' }}</dd>
        </div>
        <div>
          <dt class="text-xs font-semibold uppercase tracking-wide text-gray-500">Problems</dt>
          <dd class="text-sm font-semibold" :class="selectedProblemsCount ? 'text-red-500' : 'text-emerald-500'">
            {{ selectedProblemsCount ? `${selectedProblemsCount} collision(s)` : 'No collisions' }}
          </dd>
        </div>
      </div>
      <div v-else class="text-sm text-blue-600">
        Click an event to see details and detected input collisions.
      </div>
    </section>

    <section ref="host" class="resource-view-timeline relative min-h-0 flex-1 overflow-hidden ">
      <svg ref="topAxisSvg" class="block w-full shrink-0 border-b border-gray-200"></svg>
      <div ref="scrollWrap" class="overflow-y-auto overflow-x-hidden">
        <svg ref="mainSvg" class="block w-full"></svg>
      </div>
      <svg ref="miniSvg" class="block w-full border-t border-gray-200"></svg>
    </section>
  </div>
</template>

<script>
import * as d3 from 'd3';
import moment from 'moment';

const MINUTE_MS = 60000;
const HOUR_MS = MINUTE_MS * 60;

export default {
  name: 'ResourceViewTimeline',
  props: {
    dataset: {
      type: Array,
      default: () => [],
    },
    range: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      selectedEvent: null,
      normalizedEvents: [],
      categories: [],
      currentDomain: null,
      fullDomain: null,
      resizeObserver: null,
      resizeRaf: null,
      redrawRaf: null,
      brushPreviewRaf: null,
      pendingBrushSelection: null,
      topAxisSvg: null,
      mainSvg: null,
      miniSvg: null,
      scrollWrap: null,
      mainRootGroup: null,
      xScale: null,
      xMiniScale: null,
      brush: null,
      brushGroup: null,
      syncingBrush: false,
      palette: {},
      layout: {
        topAxisHeight: 52,
        miniHeight: 58,
        leftGutter: 138,
        rightPadding: 16,
        groupHeader: 22,
        groupGap: 12,
        rowHeight: 38,
        rowGap: 8,
        cardRadius: 6,
        minFrontWidth: 10,
      },
    };
  },
  computed: {
    selectedProblemsCount() {
      if (!this.selectedEvent || !this.selectedEvent.overlappingInputs) return 0;
      return this.selectedEvent.overlappingInputs.length;
    },
    normalizedRange() {
      const start = this.range.start || this.range.startDate;
      const end = this.range.end || this.range.endDate;
      return {
        start: start ? moment.utc(start).toDate() : null,
        end: end ? moment.utc(end).toDate() : null,
      };
    },
  },
  watch: {
    dataset: {
      deep: true,
      handler() {
        this.queueRedraw(true);
      },
    },
    range: {
      deep: true,
      handler() {
        this.queueRedraw(true);
      },
    },
  },
  mounted() {
    this.topAxisSvg = d3.select(this.$refs.topAxisSvg);
    this.mainSvg = d3.select(this.$refs.mainSvg);
    this.miniSvg = d3.select(this.$refs.miniSvg);
    this.scrollWrap = this.$refs.scrollWrap;
    this.queueRedraw(true);
    this.setupResizeObserver();
  },
  beforeDestroy() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
    if (this.redrawRaf) cancelAnimationFrame(this.redrawRaf);
    if (this.brushPreviewRaf) cancelAnimationFrame(this.brushPreviewRaf);
  },
  methods: {
    formatUtc(value) {
      if (!value) return '—';
      return moment.utc(value).format('YYYY/MM/DD HH:mm');
    },
    formatShortTime(value) {
      if (!value) return '--:--';
      return d3.utcFormat('%H:%M')(new Date(value));
    },
    escapeHtml(value) {
      if (value === null || value === undefined) return '';
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    },
    setupResizeObserver() {
      if (typeof ResizeObserver === 'undefined') return;
      this.resizeObserver = new ResizeObserver(() => {
        if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
        this.resizeRaf = requestAnimationFrame(() => {
          this.resizeRaf = null;
          this.queueRedraw(false);
        });
      });
      this.resizeObserver.observe(this.$refs.host);
    },
    queueRedraw(resetDomain) {
      if (this.redrawRaf) cancelAnimationFrame(this.redrawRaf);
      this.redrawRaf = requestAnimationFrame(() => {
        this.redrawRaf = null;
        this.renderTimeline(resetDomain);
      });
    },
    getPalette() {
      return {
        cardBg: '#ffffff',
        mutedBg: '#f8fafc',
        axis: '#64748b',
        grid: '#dbe2ea',
        category: '#475569',
        dayStripe: 'rgba(15,23,42,0.04)',
        eventBackStroke: '#ffffff',
        eventLabel: '#111827',
        miniTrack: '#f8fafc',
        nowLine: '#f97316',
        selection: 'rgba(249,115,22,0.26)',
      };
    },
    statusColor(status) {
      const mapping = {
        COMPLETED: '#0033ff',
        CONFIRMED: '#90ee90',
        CLIENT_CONFIRMED: '#ff9000',
        RECEIVED: '#ea3cf7',
        LIVE: '#00ff00',
        PENDING: '#fffe54',
        CANCELLED: '#999999',
        LATE_CANCELLED: '#8b0000',
        POSTPONED: '#999999',
        FAILED: '#ff0000',
        AWAITING_SIGNAL: '#6cdf6c',
      };
      return mapping[status] || '#38bdf8';
    },
    normalizeDataset() {
      const events = (this.dataset || [])
        .map((item, index) => {
          const serviceStartTime = moment.utc(item.serviceStartTime).toDate();
          const serviceEndTime = moment.utc(item.serviceEndTime).toDate();
          const startTime = item.startTime ? moment.utc(item.startTime).toDate() : serviceStartTime;
          const endTime = item.endTime ? moment.utc(item.endTime).toDate() : serviceEndTime;
          if (!serviceStartTime || !serviceEndTime || Number.isNaN(serviceStartTime.getTime()) || Number.isNaN(serviceEndTime.getTime())) {
            return null;
          }
          return {
            ...item,
            _id: item.id || `${item.fixture || 'event'}-${index}`,
            serviceStartTime,
            serviceEndTime,
            startTime,
            endTime,
            inputName: item.inputName || 'Unknown input',
            overlappingInputs: item.overlappingInputs || [],
          };
        })
        .filter(Boolean)
        .sort((a, b) => a.serviceStartTime - b.serviceStartTime || a.serviceEndTime - b.serviceEndTime);

      this.normalizedEvents = events;
      this.categories = [...new Set(events.map((event) => event.inputName))].sort((a, b) => a.localeCompare(b));
    },
    resolveDomains(resetDomain) {
      const starts = this.normalizedEvents.map((event) => event.serviceStartTime);
      const ends = this.normalizedEvents.map((event) => event.serviceEndTime);
      const fallbackStart = moment.utc().subtract(1, 'days').startOf('day').toDate();
      const fallbackEnd = moment.utc().add(1, 'days').endOf('day').toDate();

      const rangeStart = this.normalizedRange.start;
      const rangeEnd = this.normalizedRange.end;
      const minEvent = starts.length ? d3.min(starts) : null;
      const maxEvent = ends.length ? d3.max(ends) : null;

      const fullStart = rangeStart || minEvent || fallbackStart;
      const fullEnd = rangeEnd || maxEvent || fallbackEnd;
      const safeEnd = fullEnd > fullStart ? fullEnd : new Date(fullStart.getTime() + HOUR_MS);

      this.fullDomain = [fullStart, safeEnd];

      if (resetDomain || !this.currentDomain) {
        this.currentDomain = [...this.fullDomain];
        return;
      }

      const [currentStart, currentEnd] = this.currentDomain;
      const domainSpan = currentEnd - currentStart;
      const fullSpan = this.fullDomain[1] - this.fullDomain[0];
      if (domainSpan > fullSpan || currentStart < this.fullDomain[0] || currentEnd > this.fullDomain[1]) {
        this.currentDomain = [...this.fullDomain];
      }
    },
    computeEventLayout() {
      const grouped = d3.group(this.normalizedEvents, (event) => event.inputName || 'Unknown input');
      const { groupHeader, groupGap, rowHeight, rowGap } = this.layout;
      const output = [];
      let yOffset = 10;

      this.categories.forEach((category) => {
        const items = (grouped.get(category) || [])
          .slice()
          .sort((a, b) => a.serviceStartTime - b.serviceStartTime || a.serviceEndTime - b.serviceEndTime);

        const lanes = [];
        items.forEach((event) => {
          let laneIndex = lanes.findIndex((laneEnd) => laneEnd <= event.serviceStartTime);
          if (laneIndex === -1) {
            lanes.push(event.serviceEndTime);
            laneIndex = lanes.length - 1;
          } else {
            lanes[laneIndex] = event.serviceEndTime;
          }

          output.push({
            ...event,
            _laneIndex: laneIndex,
            _category: category,
            _groupTop: yOffset,
            _y: yOffset + groupHeader + laneIndex * (rowHeight + rowGap),
          });
        });

        const laneCount = Math.max(1, lanes.length);
        yOffset += groupHeader + laneCount * (rowHeight + rowGap) + groupGap;
      });

      return {
        events: output,
        chartHeight: Math.max(yOffset + 16, 220),
      };
    },
    measureDimensions() {
      const host = this.$refs.host;
      if (!host) return null;
      const width = host.clientWidth;
      const height = host.clientHeight;
      if (!width || !height) return null;
      return { width, height };
    },
    renderTimeline(resetDomain = false) {
      this.normalizeDataset();
      this.resolveDomains(resetDomain);

      const dims = this.measureDimensions();
      if (!dims) return;

      this.palette = this.getPalette();
      const { width, height } = dims;
      const { topAxisHeight, miniHeight, leftGutter, rightPadding } = this.layout;
      const scrollHeight = Math.max(180, height - topAxisHeight - miniHeight);
      this.scrollWrap.style.height = `${scrollHeight}px`;

      const layoutData = this.computeEventLayout();
      const viewStart = leftGutter;
      const viewEnd = Math.max(viewStart + 40, width - rightPadding);

      this.xScale = d3.scaleUtc().domain(this.currentDomain).range([viewStart, viewEnd]);
      this.xMiniScale = d3.scaleUtc().domain(this.fullDomain).range([viewStart, viewEnd]);

      this.renderTopAxis(width, topAxisHeight);
      this.renderMainChart(width, layoutData.chartHeight, layoutData.events);
      this.renderMiniMap(width, miniHeight);
    },
    renderTopAxis(width, topAxisHeight) {
      this.topAxisSvg.selectAll('*').remove();
      this.topAxisSvg
        .attr('width', width)
        .attr('height', topAxisHeight)
        .style('background', this.palette.cardBg);

      const axis = d3.axisTop(this.xScale)
        .ticks(Math.max(3, Math.floor((width - this.layout.leftGutter) / 120)))
        .tickFormat(d3.utcFormat('%d/%m %H:%M'))
        .tickSizeOuter(0)
        .tickSizeInner(6);

      const topAxisGroup = this.topAxisSvg
        .append('g')
        .attr('transform', `translate(0, ${topAxisHeight - 10})`)
        .call(axis);

      topAxisGroup.selectAll('text').attr('fill', this.palette.axis).attr('font-size', 10);
      topAxisGroup.selectAll('line').attr('stroke', this.palette.grid);
      topAxisGroup.select('.domain').attr('stroke', this.palette.grid);
    },
    renderMainChart(width, chartHeight, events) {
      this.mainSvg.selectAll('*').remove();
      this.mainSvg
        .attr('width', width)
        .attr('height', chartHeight)
        .style('background', this.palette.mutedBg)
        .on('wheel', (event) => this.handleWheelZoom(event));

      this.mainRootGroup = this.mainSvg.append('g');
      this.drawDayStripes(chartHeight);
      this.drawGroupGuides(events, chartHeight);
      this.drawNowLine(chartHeight);
      this.drawEvents(events);
    },
    drawDayStripes(chartHeight) {
      const [domainStart, domainEnd] = this.xScale.domain();
      const dayStarts = d3.utcDay.range(d3.utcDay.floor(domainStart), d3.utcDay.offset(domainEnd, 1));
      const isStripedDay = (day) => Math.floor(day.getTime() / 86400000) % 2 === 0;
      this.mainRootGroup
        .append('g')
        .selectAll('rect')
        .data(dayStarts)
        .join('rect')
        .attr('class', 'day-stripe')
        .attr('x', (day) => this.xScale(day))
        .attr('y', 0)
        .attr('width', (day) => this.xScale(d3.utcDay.offset(day, 1)) - this.xScale(day))
        .attr('height', chartHeight)
        .attr('fill', (day) => (isStripedDay(day) ? this.palette.dayStripe : 'transparent'));
    },
    drawGroupGuides(events, chartHeight) {
      const grouped = d3.group(events, (event) => event._category);
      const groupLayer = this.mainRootGroup.append('g');

      [...grouped.keys()].forEach((category) => {
        const groupEvents = grouped.get(category) || [];
        if (!groupEvents.length) return;
        const groupTop = groupEvents[0]._groupTop;
        const groupBottom = d3.max(groupEvents, (event) => event._y + this.layout.rowHeight) + this.layout.groupGap;

        groupLayer.append('line')
          .attr('x1', this.layout.leftGutter)
          .attr('x2', this.xScale.range()[1])
          .attr('y1', groupBottom)
          .attr('y2', groupBottom)
          .attr('stroke', this.palette.grid)
          .attr('shape-rendering', 'crispEdges');

        groupLayer.append('text')
          .attr('x', 10)
          .attr('y', groupTop + 15)
          .attr('fill', this.palette.category)
          .attr('font-size', 11)
          .attr('font-weight', 700)
          .text(category.toUpperCase());
      });

      groupLayer.append('line')
        .attr('x1', this.layout.leftGutter)
        .attr('x2', this.layout.leftGutter)
        .attr('y1', 0)
        .attr('y2', chartHeight)
        .attr('stroke', this.palette.grid)
        .attr('shape-rendering', 'crispEdges');
    },
    drawNowLine(chartHeight) {
      const nowX = this.xScale(new Date());
      if (nowX < this.layout.leftGutter || nowX > this.xScale.range()[1]) return;

      this.mainRootGroup.append('line')
        .attr('class', 'now-line')
        .attr('x1', nowX)
        .attr('x2', nowX)
        .attr('y1', 0)
        .attr('y2', chartHeight)
        .attr('stroke', this.palette.nowLine)
        .attr('stroke-width', 1.5)
        .attr('stroke-dasharray', '3 3');

      this.mainRootGroup.append('circle')
        .attr('class', 'now-dot')
        .attr('cx', nowX)
        .attr('cy', 8)
        .attr('r', 4)
        .attr('fill', this.palette.nowLine)
        .attr('stroke', '#ffffff')
        .attr('stroke-width', 1.5);
    },
    drawEvents(events) {
      const eventLayer = this.mainRootGroup.append('g').attr('class', 'event-layer');
      const frontHeight = Math.max(14, this.layout.rowHeight - 3);

      const groups = eventLayer
        .selectAll('g.event-item')
        .data(events, (event) => event._id)
        .join('g')
        .attr('class', (event) => `event-item ${event.status || ''}`)
        .attr('transform', (event) => `translate(0, ${event._y})`)
        .style('cursor', 'pointer')
        .on('click', (_, event) => {
          this.selectedEvent = event;
          this.paintSelection();
        })
        .on('mouseenter', (_, event) => {
          this.selectedEvent = event;
          this.paintSelection();
        });

      groups
        .append('rect')
        .attr('class', 'event-back')
        .attr('x', (event) => this.xScale(event.serviceStartTime))
        .attr('y', 0)
        .attr('width', (event) => {
          const backStart = d3.min([event.serviceStartTime, event.startTime]);
          const backEnd = d3.max([event.serviceEndTime, event.endTime]);
          return Math.max(8, this.xScale(backEnd) - this.xScale(backStart));
        })
        .attr('height', this.layout.rowHeight)
        .attr('rx', this.layout.cardRadius)
        .attr('ry', this.layout.cardRadius)
        .attr('fill', (event) => d3.interpolate('#ffffff', this.statusColor(event.status))(0.35))
        .attr('stroke', this.palette.eventBackStroke)
        .attr('stroke-width', 2);

      groups
        .append('rect')
        .attr('class', 'event-front')
        .attr('x', (event) => this.xScale(event.startTime))
        .attr('y', (this.layout.rowHeight - frontHeight) / 2)
        .attr('width', (event) => Math.max(this.layout.minFrontWidth, this.xScale(event.endTime) - this.xScale(event.startTime)))
        .attr('height', frontHeight)
        .attr('rx', this.layout.cardRadius - 1)
        .attr('ry', this.layout.cardRadius - 1)
        .attr('fill', (event) => this.statusColor(event.status));

      groups
        .append('foreignObject')
        .attr('class', 'event-fo')
        .attr('x', (event) => this.xScale(event.startTime) + 6)
        .attr('y', 3)
        .attr('width', (event) => Math.max(4, this.xScale(event.endTime) - this.xScale(event.startTime) - 12))
        .attr('height', this.layout.rowHeight - 6)
        .append('xhtml:div')
        .attr('class', 'event-content')
        .html((event) => this.eventContentHtml(event));

      groups
        .filter((event) => event.overlappingInputs && event.overlappingInputs.length > 0)
        .append('circle')
        .attr('cx', (event) => this.xScale(event.serviceEndTime) - 7)
        .attr('cy', 7)
        .attr('r', 4)
        .attr('fill', '#ef4444')
        .attr('stroke', '#ffffff')
        .attr('stroke-width', 1.5);

      this.paintSelection();
    },
    fitLabel(label, event, pxPerChar = 6.2) {
      const width = Math.max(this.layout.minFrontWidth, this.xScale(event.endTime) - this.xScale(event.startTime));
      const maxChars = Math.max(4, Math.floor((width - 12) / pxPerChar));
      if (label.length <= maxChars) return label;
      return `${label.slice(0, maxChars - 1)}…`;
    },
    eventContentHtml(event) {
      return `
        <div class="event-line-1"><b>${this.formatShortTime(event.startTime)} - ${this.formatShortTime(event.endTime)}</b> ${this.escapeHtml(this.fitLabel(event.name || event.fixture || event.title || 'Event', event, 8))}</div>
        <div class="event-line-2">${this.escapeHtml(this.fitLabel(event.description || 'No description', event, 7))}</div>
      `;
    },
    updateMainForDomain(refreshLabels = false) {
      if (!this.mainSvg || !this.xScale) return;

      const [domainStart, domainEnd] = this.xScale.domain();
      const dayStarts = d3.utcDay.range(d3.utcDay.floor(domainStart), d3.utcDay.offset(domainEnd, 1));
      const isStripedDay = (day) => Math.floor(day.getTime() / 86400000) % 2 === 0;

      this.mainSvg.selectAll('.day-stripe')
        .data(dayStarts)
        .join(
          (enter) => enter.append('rect')
            .attr('class', 'day-stripe')
            .attr('y', 0),
          (update) => update,
          (exit) => exit.remove(),
        )
        .attr('x', (day) => this.xScale(day))
        .attr('width', (day) => this.xScale(d3.utcDay.offset(day, 1)) - this.xScale(day))
        .attr('height', this.mainSvg.attr('height'))
        .attr('fill', (day) => (isStripedDay(day) ? this.palette.dayStripe : 'transparent'));

      const nowX = this.xScale(new Date());
      this.mainSvg.select('.now-line').attr('x1', nowX).attr('x2', nowX);
      this.mainSvg.select('.now-dot').attr('cx', nowX);

      this.mainSvg.selectAll('rect.event-back')
        .attr('x', (event) => this.xScale(event.serviceStartTime))
        .attr('width', (event) => {
          const backStart = d3.min([event.serviceStartTime, event.startTime]);
          const backEnd = d3.max([event.serviceEndTime, event.endTime]);
          return Math.max(8, this.xScale(backEnd) - this.xScale(backStart));
        });

      this.mainSvg.selectAll('rect.event-front')
        .attr('x', (event) => this.xScale(event.startTime))
        .attr('width', (event) => Math.max(this.layout.minFrontWidth, this.xScale(event.endTime) - this.xScale(event.startTime)));

      this.mainSvg.selectAll('foreignObject.event-fo')
        .attr('x', (event) => this.xScale(event.startTime) + 6)
        .attr('width', (event) => Math.max(4, this.xScale(event.endTime) - this.xScale(event.startTime) - 12));

      this.mainSvg.selectAll('g.event-item circle')
        .attr('cx', (event) => this.xScale(event.serviceEndTime) - 7);

      if (refreshLabels) {
        this.mainSvg.selectAll('foreignObject.event-fo .event-content')
          .html((event) => this.eventContentHtml(event));
      }

      this.renderTopAxis(this.$refs.host.clientWidth, this.layout.topAxisHeight);
      this.paintSelection();
    },
    paintSelection() {
      const selectedId = this.selectedEvent ? this.selectedEvent._id : null;
      this.mainSvg
        .selectAll('g.event-item')
        .classed('is-selected', (event) => event._id === selectedId);

      this.mainSvg
        .selectAll('g.event-item .event-back')
        .attr('stroke-width', (event) => (event._id === selectedId ? 3 : 2))
        .attr('stroke', (event) => (event._id === selectedId ? this.palette.nowLine : this.palette.eventBackStroke));
    },
    handleWheelZoom(event) {
      event.preventDefault();
      const [domainStart, domainEnd] = this.currentDomain;
      const span = domainEnd - domainStart;
      const minSpan = 30 * MINUTE_MS;
      const maxSpan = this.fullDomain[1] - this.fullDomain[0];
      const factor = event.deltaY > 0 ? 1.18 : 0.84;
      let nextSpan = span * factor;
      nextSpan = Math.min(maxSpan, Math.max(minSpan, nextSpan));

      const [mx] = d3.pointer(event, this.$refs.mainSvg);
      const center = this.xScale.invert(mx);
      let nextStart = new Date(center.getTime() - nextSpan / 2);
      let nextEnd = new Date(center.getTime() + nextSpan / 2);

      if (nextStart < this.fullDomain[0]) {
        nextStart = this.fullDomain[0];
        nextEnd = new Date(nextStart.getTime() + nextSpan);
      }
      if (nextEnd > this.fullDomain[1]) {
        nextEnd = this.fullDomain[1];
        nextStart = new Date(nextEnd.getTime() - nextSpan);
      }

      this.currentDomain = [nextStart, nextEnd];
      this.renderTimeline(false);
      this.syncBrushToDomain();
    },
    renderMiniMap(width, miniHeight) {
      this.miniSvg.selectAll('*').remove();
      this.miniSvg
        .attr('width', width)
        .attr('height', miniHeight)
        .style('background', this.palette.miniTrack);

      const miniRootGroup = this.miniSvg.append('g');

      miniRootGroup
        .append('g')
        .selectAll('circle')
        .data(this.normalizedEvents, (event) => event._id)
        .join('circle')
        .attr('cx', (event) => this.xMiniScale(event.startTime))
        .attr('cy', miniHeight / 2 - 6)
        .attr('r', (event) => (event.overlappingInputs.length > 0 ? 3.3 : 2.4))
        .attr('fill', (event) => (event.overlappingInputs.length > 0 ? '#ef4444' : this.statusColor(event.status)))
        .attr('opacity', 0.9);

      miniRootGroup
        .append('g')
        .attr('transform', `translate(0, ${miniHeight - 2})`)
        .call(
          d3.axisBottom(this.xMiniScale)
            .ticks(Math.max(2, Math.floor((width - this.layout.leftGutter) / 140)))
            .tickFormat(d3.utcFormat('%d/%m'))
            .tickSize(3),
        )
        .call((g) => {
          g.selectAll('text').attr('fill', this.palette.axis).attr('font-size', 10);
          g.selectAll('line').attr('stroke', this.palette.grid);
          g.select('.domain').attr('stroke', this.palette.grid);
        });

      this.brush = d3
        .brushX()
        .extent([[this.layout.leftGutter, 6], [this.xMiniScale.range()[1], miniHeight - 11]])
        .on('brush', (evt) => this.handleBrushMove(evt))
        .on('end', (evt) => this.handleBrushEnd(evt));

      this.brushGroup = miniRootGroup
        .append('g')
        .attr('class', 'resource-brush')
        .call(this.brush);

      this.styleBrushElements(miniHeight);
      this.syncBrushToDomain();
    },
    styleBrushElements(miniHeight) {
      if (!this.brushGroup) return;
      const brushTop = 6;
      const brushHeight = miniHeight - 17;
      this.brushGroup.selectAll('.overlay').attr('cursor', 'crosshair');
      // this.brushGroup.selectAll('.selection')
      //   .attr('y', brushTop)
      //   .attr('height', brushHeight)
      //   .attr('rx', 3)
      //   .attr('ry', 3)
      //   .attr('fill', this.palette.selection)
      //   .attr('stroke', this.palette.nowLine)
      //   .attr('stroke-width', 1.2);
      // this.brushGroup.selectAll('.handle')
      //   .attr('y', brushTop)
      //   .attr('height', brushHeight)
      //   .attr('width', 6)
      //   .attr('rx', 3)
      //   .attr('ry', 3)
      //   .attr('fill', this.palette.nowLine);
    },
    syncBrushToDomain() {
      if (!this.brushGroup || !this.brush || !this.currentDomain || !this.xMiniScale) return;
      this.syncingBrush = true;
      const selection = this.currentDomain.map((date) => this.xMiniScale(date));
      this.brushGroup.call(this.brush.move, selection);
      this.styleBrushElements(this.layout.miniHeight);
      this.syncingBrush = false;
    },
    handleBrushMove(event) {
      if (this.syncingBrush || !event.selection) return;
      this.pendingBrushSelection = event.selection;
      if (this.brushPreviewRaf) return;

      this.brushPreviewRaf = requestAnimationFrame(() => {
        this.brushPreviewRaf = null;
        if (!this.pendingBrushSelection) return;
        const [startX, endX] = this.pendingBrushSelection;
        const start = this.xMiniScale.invert(startX);
        const end = this.xMiniScale.invert(endX);
        if (!start || !end || end <= start) return;

        this.currentDomain = [start, end];
        this.xScale.domain(this.currentDomain);
        this.updateMainForDomain(false);
      });
    },
    handleBrushEnd(event) {
      if (this.syncingBrush || !event.selection) return;
      const [startX, endX] = event.selection;
      const start = this.xMiniScale.invert(startX);
      const end = this.xMiniScale.invert(endX);
      if (!start || !end || end <= start) return;

      this.currentDomain = [start, end];
      this.xScale.domain(this.currentDomain);
      this.updateMainForDomain(true);
      this.styleBrushElements(this.layout.miniHeight);
    },
  },
};
</script>

<style scoped>
.resource-view-timeline :deep(.tick text) {
  font-family: inherit;
}

.resource-view-timeline :deep(.event-item.is-selected .event-front) {
  filter: brightness(1.05);
}

.resource-view-timeline :deep(.event-content) {
  color: #111827;
  font-size: 11px;
  line-height: 1.15;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.resource-view-timeline :deep(.event-line-1),
.resource-view-timeline :deep(.event-line-2) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.resource-view-timeline :deep(.event-line-2) {
  opacity: 0.92;
  font-size: 10px;
}

.resource-view-timeline :deep(.resource-brush .selection) {
  rx: 4;
  ry: 4;
}

resource-selection-info {
  grid-template-columns: repeat(8, minmax(0, 1fr));
}
@media screen and (max-width: 640px) {
  .resource-selection-info {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  
}
</style>
